import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Header, Platform } from '@aptsi-types';
import { PlatformService } from '../../services/platform.service';
import { DeleteDialogComponent } from 'src/app/shared/dialogs/delete-dialog/delete-dialog.component';
import { WorkflowNavigationService } from '../../../shared/workflow-navigation.service';

type PlatformWithPagination = Platform & {
  totalPlatforms?: number;
  totalItems?: number;
  totalCount?: number;
  totalPages?: number;
  currentPage?: number;
};

@Component({
  selector: 'app-platform-search',
  templateUrl: './platform-search.component.html',
  styleUrls: ['./platform-search.component.scss']
})
export class PlatformSearchComponent implements OnInit {
  totalPages = 0;
  totalPagesNormal = 0;
  totalPagesSearch = 0;
  totalItemsNormal = 0;
  totalItemsSearch = 0;

  readonly itemsPerPage = 7;
  currentPage = 1;
  isAdd = false;
  isSearch = false;
  isLoading = true;
  isSearching = false;
  loadError = false;
  searchError = false;

  filteredData: Platform[] = [];
  searchedData: Platform[] = [];

  get displayedData(): Platform[] {
    return this.isSearch ? this.searchedData : this.filteredData;
  }

  headers: Header[] = [
    {
      name: 'Platform ID',
      property: 'id',
      showInDropdown: true,
      description: 'Unique identifier for the platform'
    },
    {
      name: 'Platform Name',
      property: 'name',
      showInDropdown: true,
      description: 'Name of the platform'
    },
    {
      name: 'Category',
      property: 'category',
      showInDropdown: true,
      description: 'Category of the platform'
    },
    {
      name: 'Description',
      property: 'description',
      showInDropdown: false,
      description: 'Description of the platform'
    },
    {
      name: 'Last Updated',
      property: 'updatedAt',
      showInDropdown: true,
      description: 'Timestamp of the most recent update'
    }
  ];

  searchText = '';
  filterByHeader = 'all';
  platform: Platform[] = [];
  nullver = 1;

  buttonConfig = {
    update: true,
    delete: true,
    import: false,
    configure: false
  };

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
     private readonly workflowNavigation: WorkflowNavigationService,
    private readonly platformService: PlatformService,
    private readonly dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.isAdd = params['isAdd'] === 'true';
    });

    void this.fetchPlatforms();
  }

  handleAction(event: { action: string; rowIndex: number; rowData: any }): void {
    if (event.action === 'update') {
      void this.updatePlatform(event.rowIndex);
    } else if (event.action === 'delete') {
      void this.deletePlatform(event.rowIndex);
    }
  }

  async fetchPlatforms(): Promise<void> {
    this.isLoading = true;
    this.loadError = false;

    try {
      const apiResponse = await this.platformService
        .getPlatforms(this.currentPage - 1)
        .toPromise();
      const response: Platform[] = Array.isArray(apiResponse) ? apiResponse : [];
      const metadata = response[0] as PlatformWithPagination | undefined;

      this.filteredData = [...response];
      this.totalPagesNormal = this.readNonNegativeNumber(
        metadata?.totalPages,
        response.length > 0 ? 1 : 0
      );
      this.totalItemsNormal = this.readNonNegativeNumber(
        metadata?.totalPlatforms ?? metadata?.totalItems ?? metadata?.totalCount,
        response.length
      );
      this.totalPages = this.totalPagesNormal;

      if (typeof metadata?.currentPage === 'number' && metadata.currentPage >= 0) {
        this.currentPage = metadata.currentPage + 1;
      } else if (this.totalPagesNormal === 0) {
        this.currentPage = 1;
      }
    } catch (error) {
      console.error('Error fetching platforms:', error);
      this.loadError = true;
      this.filteredData = [];
      this.totalPagesNormal = 0;
      this.totalItemsNormal = 0;
      this.totalPages = 0;
    } finally {
      this.isLoading = false;
    }
  }

  async fetchPlatformsSearch(): Promise<void> {
    this.isSearching = true;
    this.searchError = false;

    try {
      const apiResponse = await this.platformService
        .getPlatformsSearch(
          this.currentPage - 1,
          this.searchText.trim(),
          this.filterByHeader
        )
        .toPromise();

      const response: Platform[] = Array.isArray(apiResponse) ? apiResponse : [];
      const metadata = response[0] as PlatformWithPagination | undefined;
      this.searchedData = [...response];

      this.totalPagesSearch = this.readNonNegativeNumber(
        metadata?.totalPages,
        response.length > 0 ? 1 : 0
      );
      this.totalItemsSearch = this.readNonNegativeNumber(
        metadata?.totalPlatforms ?? metadata?.totalItems ?? metadata?.totalCount,
        response.length
      );
      this.totalPages = this.totalPagesSearch;

      if (typeof metadata?.currentPage === 'number' && metadata.currentPage >= 0) {
        this.currentPage = metadata.currentPage + 1;
      }
    } catch (error) {
      console.error('Error searching platforms:', error);
      this.searchError = true;
      this.searchedData = [];
      this.totalPagesSearch = 0;
      this.totalItemsSearch = 0;
      this.totalPages = 0;
    } finally {
      this.isSearching = false;
    }
  }

  async searchPlatforms(): Promise<void> {
    const query = this.searchText.trim();

    if (!query) {
      await this.clearSearch();
      return;
    }

    this.searchText = query;
    this.isSearch = true;
    this.currentPage = 1;
    await this.fetchPlatformsSearch();

    // Keep the existing client-side filter behavior after the search request.
    const selectedHeader = this.headers.find(
      header => header.property === this.filterByHeader && header.showInDropdown
    );

    if (this.filterByHeader === 'all') {
      this.searchedData = this.searchedData.filter(platform =>
        this.headers.some(header => {
          const value = (platform as any)[header.property];
          return value?.toString().toLowerCase().includes(this.searchText.toLowerCase());
        })
      );
    } else if (selectedHeader) {
      this.searchedData = this.searchedData.filter(platform => {
        const value = (platform as any)[selectedHeader.property];
        return value?.toString().toLowerCase().includes(this.searchText.toLowerCase());
      });
      this.searchedData.sort((left, right) => {
        const leftValue = (left as any)[selectedHeader.property]?.toString() ?? '';
        const rightValue = (right as any)[selectedHeader.property]?.toString() ?? '';
        return leftValue.localeCompare(rightValue);
      });
    }
  }

  onInputChange(event: Event): void {
    this.searchText = (event.target as HTMLInputElement).value;
  }

  onFilterChange(): void {
    this.currentPage = 1;
    if (this.searchText.trim()) {
      this.isSearch = true;
      void this.searchPlatforms();
    } else {
      void this.clearSearch();
    }
  }

  async clearSearch(): Promise<void> {
    this.searchText = '';
    this.isSearch = false;
    this.searchError = false;
    this.currentPage = 1;
    this.totalPages = this.totalPagesNormal;
    await this.fetchPlatforms();
  }

  resetSearchText(): void {
    this.searchText = '';
    this.isSearch = false;
    this.currentPage = 1;
  }

  changePage(page: number): void {
    if (!Number.isFinite(page) || page < 1 || page === this.currentPage) {
      return;
    }

    this.currentPage = page;
    if (this.isSearch) {
      void this.fetchPlatformsSearch();
    } else {
      void this.fetchPlatforms();
    }
  }

/**
 * Navigate to the platform creation form.
 */
    addPlatform(): void {
      void this.workflowNavigation.navigateToWorkflow(
        'securityZoneManagement',
        ['platform', 'create']
      );
    }

  async deletePlatform(index: number): Promise<void> {
    try {
      const dialogRef = this.dialog.open(DeleteDialogComponent, {
        data: {
          title: 'Are you sure?',
          message: 'Do you really want to delete this Platform?',
          isConfirm: false
        }
      });

      const confirmed = await dialogRef.afterClosed().toPromise();
      if (!confirmed) return;

      const platformId = this.displayedData[index]?.id;
      if (platformId === null || platformId === undefined || platformId === '') return;

      await this.platformService.deletePlatform(platformId).toPromise();

      if (this.isSearch) {
        await this.fetchPlatformsSearch();
      } else {
        await this.fetchPlatforms();
      }

      this.dialog.open(DeleteDialogComponent, {
        data: {
          title: 'Success',
          message: 'The Platform has been deleted successfully.',
          isConfirm: true
        }
      });
    } catch (error) {
      console.error('Error deleting platform:', error);
    }
  }

/**
 * Navigate to the platform update form.
 */
    updatePlatform(index: number): void {
      const platformId = this.displayedData[index]?.id;

      if (
        platformId === null ||
        platformId === undefined ||
        platformId === ''
      ) {
        return;
      }

      void this.workflowNavigation.navigateToWorkflow(
        'securityZoneManagement',
        ['platform', 'update', String(platformId)]
      );
    }

    back(): void {
      if (this.isSearch) {
        void this.clearSearch();
        return;
      }

      void this.workflowNavigation.navigateToProductHome();
    }

  private readNonNegativeNumber(value: unknown, fallback: number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
  }
}
